import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core'
import {JsonPipe} from '@angular/common'
import {BetslipWorkbenchStore, WorkbenchLimits} from './betslip-workbench.store'
import {WorkbenchRiskService} from './workbench-risk.service'
import {LiabilityLimit, RiskInfoStatus} from './mock-betslip-risk-backend'
import {OfferSelection, offerSelection, pairKey} from './demo-offer'
import {LiabilityLimitsState, PlayerLiabilityTier} from './liability-limits.state'

enum LimitsTab {
	betting = 'betting',
	liability = 'liability'
}

type NumericLimitKey = Exclude<keyof WorkbenchLimits, 'maxStakeToWinMaximumPayout'>

/**
 * The operator's settings. Betting limits feed the isolated betslip's site config; liability
 * limits configure the mock backend whose reply the betslip receives as risk info.
 */
@Component({
	selector: 'dt-workbench-limits-panel',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [JsonPipe],
	styles: `
		fieldset.player-tier { margin: 0 0 16px; padding: 8px 10px; border: 1px solid #2a3647; border-radius: 6px; }
		fieldset.player-tier legend { padding: 0 4px; color: #cfdae8; }
		fieldset.player-tier label.check { margin-top: 6px; align-items: center; }
		fieldset.player-tier label.check em { display: inline; margin-left: 6px; }
	`,
	template: `
		<h2>Limits</h2>

		<fieldset class="player-tier">
			<legend>Player's betslip</legend>
			<em class="muted">Decides, on both betslips, above which stake it goes for authorization and where it is capped.</em>
			@for (option of playerTiers; track option.tier) {
				<label class="check">
					<input type="radio" name="player-liability-tier" [checked]="liability.playerTier() === option.tier"
						   (change)="liability.playerTier.set(option.tier)">
					<span>{{ option.label }} <em>{{ option.hint }}</em></span>
				</label>
			}
		</fieldset>

		<nav class="tabs">
			<button type="button" [class.active]="active() === LimitsTab.betting" (click)="active.set(LimitsTab.betting)">
				Betting limits
			</button>
			<button type="button" [class.active]="active() === LimitsTab.liability" (click)="active.set(LimitsTab.liability)">
				Liability limits
			</button>
		</nav>

		@if (active() === LimitsTab.betting) {
			<p class="hint">
				Fed to the betslip as <code>SiteConfigService.bettingRules</code>. <strong>0 means no limit</strong>.
			</p>

			<table class="pairs">
				<thead>
					<tr>
						<th>Stake Limit</th>
						<th>Limit</th>
						<th>VIP</th>
					</tr>
				</thead>
				<tbody>
					@for (row of tieredFields; track row.label) {
						<tr>
							<td>{{ row.label }}</td>
							<td>
								<input type="number" min="0" step="10" [value]="limits()[row.limit]"
									   (change)="patch(row.limit, $event)">
							</td>
							<td>
								<input type="number" min="0" step="10" [value]="limits()[row.vip]"
									   (change)="patch(row.vip, $event)">
							</td>
						</tr>
					}
				</tbody>
			</table>

			<p class="hint">
				Which column is the Auto-accept limit and which the cap follows the player picked above. A VIP
				limit left at 0 sits on the Limit. A Betslip carrying both kinds is bound by both, so the smaller
				one wins.
			</p>

			@for (field of fields; track field.key) {
				<label>
					{{ field.label }} @if (field.tier) { <span class="muted">({{ field.tier }})</span> }
					<input type="number" min="0" [step]="field.step" [value]="limits()[field.key]"
						   (change)="patch(field.key, $event)">
					@if (field.hint) { <em>{{ field.hint }}</em> }
				</label>
			}

			<label class="check">
				<input type="checkbox" [checked]="limits().maxStakeToWinMaximumPayout" (change)="toggleFlag($event)">
				<span>
					Betting rule: <strong>Maximum Stake to Win Maximum Payout</strong>
					<em>
						Recorded on vectors only — production has no field for it yet, so the betslip here
						ignores it.
					</em>
				</span>
			</label>

			<p class="hint">
				Production's <code>BettingLimitsDto</code> has one Auto-accept tier, so the live Auto-accept
				limit only reaches the betslip as the live VIP fallback.
			</p>
		} @else {
<!--			<p class="not-applied">-->
<!--				<strong>Mock backend.</strong> These settings answer the betslip's <code>betslipRiskInfo</code>-->
<!--				request in place of the real backend: <em>Liability / (odds &minus; 1)</em>, smallest limit of the-->
<!--				pairs a bet carries (ADR 0001). The reply reaches the betslip through-->
<!--				<code>mergeRemoteProblems</code>, and betslip-create caps a Single's and a Combo's stake at the-->
<!--				VIP ceiling.-->
<!--			</p>-->

			<details class="field" [open]="true">
				<summary>
					Last response
					@if (risk.last(); as last) { <span class="muted">— {{ last.at.toLocaleTimeString() }}</span> }
				</summary>
				@if (risk.last(); as last) {
					@if (last.error) {
						<p class="problem">{{ last.error }}</p>
					} @else if (last.response) {
						<pre>{{ last.response | json }}</pre>
					} @else {
						<p class="hint">{{ statusLabel(last.status) }} — no request sent.</p>
					}
					<details>
						<summary>Request (CreateBetslipDto)</summary>
						<pre>{{ last.request | json }}</pre>
					</details>
				} @else {
					<p class="hint">Nothing asked yet — add a Selection.</p>
				}
			</details>

			<table class="pairs">
				<thead>
					<tr>
						<th>Market-outcome pair</th>
						<th>Limit</th>
						<th>VIP</th>
					</tr>
				</thead>
				@for (market of markets(); track market.marketId) {
					<tbody>
						<tr class="market"><th colspan="3">{{ market.marketLabel }}</th></tr>
						@for (pair of market.pairs; track pair.outcomeKey) {
							<tr [class.on-slip]="onSlip().has(key(pair))">
								<td>{{ pair.outcomeLabel }}</td>
								<td>
									<input type="number" min="0" step="100" [value]="pair.liabilityLimit"
										   (change)="patchPair(pair, 'liabilityLimit', $event)">
								</td>
								<td>
									<input type="number" min="0" step="100" [value]="pair.vipLiabilityLimit"
										   (change)="patchPair(pair, 'vipLiabilityLimit', $event)">
								</td>
							</tr>
						}
					</tbody>
				}
			</table>

			<p class="hint">
				One limit per market-outcome pair, for every fixture that offers it. Highlighted rows are the
				pairs on the betslip; a bet spanning several is bound by the tightest.
			</p>
		}
	`
})
export class WorkbenchLimitsPanelComponent {
	readonly store = inject(BetslipWorkbenchStore)
	readonly liability = inject(LiabilityLimitsState)
	readonly risk = inject(WorkbenchRiskService)
	readonly LimitsTab = LimitsTab

	readonly active = signal(LimitsTab.betting)
	readonly limits = this.store.limits

	readonly fields: {key: NumericLimitKey, label: string, tier?: string, step: number, hint?: string}[] = [
		{key: 'minStake', label: 'Minimum Stake', step: 0.5},
		{key: 'maximumPayout', label: 'Maximum Payout', step: 1000},
		{key: 'maximumTotalOdds', label: 'Maximum Total Odds', step: 100},
		{key: 'maxCombinations', label: 'Maximum Combinations', step: 1000}
	]

	/** The Stake Limits that come in two tiers, laid out as the Liability tab's Limit and VIP columns. */
	readonly tieredFields: {label: string, limit: NumericLimitKey, vip: NumericLimitKey}[] = [
		{label: 'Prematch', limit: 'maxStake', vip: 'vipStakeLimit'},
		{label: 'Live', limit: 'liveStakeLimit', vip: 'vipLiveStakeLimit'}
	]

	readonly statuses = [
		{status: RiskInfoStatus.ready, label: 'Ready — reply with ceilings'},
		{status: RiskInfoStatus.pending, label: 'Pending — still loading'},
		{status: RiskInfoStatus.failed, label: 'Request failed'},
		{status: RiskInfoStatus.loggedOut, label: 'Player logged out'}
	]

	/** Which column prices the main betslip's hints (`PlayerLiabilityMock`); this workbench's betslip gets both. */
	readonly playerTiers = [
		{tier: PlayerLiabilityTier.normal, label: 'Normal player', hint: 'authorization above Limit, capped at VIP'},
		{tier: PlayerLiabilityTier.vip, label: 'VIP player', hint: 'authorization above VIP, no cap'},
		{tier: PlayerLiabilityTier.off, label: 'Not applied', hint: 'the site limits'}
	]

	/** The pairs grouped by market, in the offer's market order. */
	readonly markets = computed(() => {
		const groups = new Map<number, {marketId: number, marketLabel: string, pairs: LiabilityLimit[]}>()

		for (const pair of this.store.liabilityLimits()) {
			const group = groups.get(pair.marketId) ?? {marketId: pair.marketId, marketLabel: pair.marketLabel, pairs: []}
			group.pairs.push(pair)
			groups.set(pair.marketId, group)
		}

		return Array.from(groups.values())
	})

	readonly onSlip = computed(() => new Set(this.store.selections()
		.map((selection) => offerSelection(selection.oddsId))
		.filter((selection): selection is OfferSelection => !!selection)
		.map((selection) => pairKey(selection))))

	key(pair: LiabilityLimit): string {
		return pairKey(pair)
	}

	statusLabel(status: RiskInfoStatus): string {
		return this.statuses.find((option) => option.status === status)?.label ?? status
	}

	patch(key: NumericLimitKey, event: Event): void {
		const value = Number((event.target as HTMLInputElement).value)
		this.store.patchLimits({[key]: Number.isFinite(value) ? value : 0})
	}

	toggleFlag(event: Event): void {
		this.store.patchLimits({maxStakeToWinMaximumPayout: (event.target as HTMLInputElement).checked})
	}

	patchPair(pair: LiabilityLimit, key: 'liabilityLimit' | 'vipLiabilityLimit', event: Event): void {
		const value = Number((event.target as HTMLInputElement).value)
		this.store.patchLiabilityLimit(pair, {[key]: Number.isFinite(value) ? value : 0})
	}

	patchStatus(event: Event): void {
		this.store.riskStatus.set((event.target as HTMLSelectElement).value as RiskInfoStatus)
	}
}
